// DS2 Button. variant: primary (default, filled) | secondary | tertiary (outlined) | plain (text only). inverse: the
// primary, tertiary and plain looks for dark backgrounds. danger: red, for an action that deletes (not with inverse).
// isLoading: a turning ring over the label, the button disabled and aria-busy meanwhile.
import cx from "classnames";
import type { ButtonHTMLAttributes, ReactElement, Ref } from "react";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "plain";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  inverse?: boolean;
  danger?: boolean;
  isLoading?: boolean;
  ref?: Ref<HTMLButtonElement>;
};

// a <Button> element, for the props that take buttons (Title, ModalDialog)
export type ButtonElement = ReactElement<ButtonProps, typeof Button>;

export function Button({
  variant = "primary",
  inverse = false,
  danger = false,
  isLoading = false,
  className,
  type = "button",
  disabled,
  children,
  ref,
  ...props
}: ButtonProps) {
  const classes = cx(
    styles.button,
    {
      [styles.primary]: variant === "primary" && !inverse,
      [styles.outline]: variant === "tertiary",
      [styles.plain]: variant === "plain",
      [styles.inverse]: inverse && variant !== "secondary",
      [styles.danger]: danger && !inverse,
      [styles.loading]: isLoading,
    },
    className,
  );
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? <span className={styles.label}>{children}</span> : children}
      {isLoading ? <span className={styles.spinner} aria-hidden="true" /> : null}
    </button>
  );
}
